import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Dialog, RadioGroup, Transition } from '@headlessui/react';
const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;
const { Option: RadioOption, Label: RadioLabel } = RadioGroup;

import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

import { Button } from '@/components/ui/Button';
import { useBenchmarks } from '@/hooks/trades';
import { useCounterparties } from '@/hooks/counterparty';
import { useCreateCashPool, useUpdateCashPool } from '@/hooks/cash-pool';
import { buildRatePayload } from '@/services/cash-pool/cash-pool-service';
import { BENCHMARK_KEYS } from '@/hooks/trades/use-benchmarks';
import { dayCountForCurrency } from '@/services/trades/trade-calculations';
import { getErrorMessage } from '@/utils/get-error-message';
import {
  CashPool,
  CashPoolDayCount,
  CashPoolFormData,
  CashPoolProduct,
  CashPoolRateType,
} from '@/types/cash-pool.types';
import {
  describePoolTerms,
  findDuplicatePool,
} from '../utils/find-duplicate-pool';
const RATE_TYPE_SEGMENTS: { key: CashPoolRateType; label: string }[] = [
  { key: 'floating', label: 'Benchmark + spread' },
  { key: 'fixed', label: 'Fixed' },
];

const SEGMENT_CLASSES =
  'cursor-pointer rounded border px-3 py-1 text-sm transition-colors focus:outline-none';

// `active` is headlessui's focus state - arrow-key navigation moves focus
// without triggering the browser's focus-visible, so the ring keys off it.
const segmentTone = (checked: boolean, active: boolean): string =>
  clsx(
    checked
      ? 'border-blue-600 bg-blue-50 text-blue-700'
      : 'border-gray-300 text-gray-600 hover:bg-gray-50',
    active && 'ring-2 ring-blue-500'
  );

interface CashPoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** GMRA/GMSLA segment the pool is created under; ignored in edit mode. */
  product: CashPoolProduct;
  /** Pools already loaded for the segment - the duplicate check runs against these. */
  pools: CashPool[];
  /** The list failed to load, so `pools` is empty for the wrong reason: say so
   *  rather than letting "no duplicate found" and "couldn't look" render alike. */
  poolsUnavailable?: boolean;
  mode?: 'create' | 'edit';
  pool?: CashPool;
}

const buildInitialForm = (product: CashPoolProduct): CashPoolFormData => ({
  counterparty: null,
  agreement: null,
  product,
  currency: '',
  name: '',
  rate_type: 'floating',
  benchmark_name: 'sofr',
  spread_bps: '',
  fixed_rate: '',
  day_count_convention: '360',
  is_active: true,
});

const buildEditForm = (pool: CashPool): CashPoolFormData => ({
  counterparty: pool.counterparty,
  agreement: pool.agreement,
  product: pool.product,
  currency: pool.currency,
  name: pool.name,
  rate_type: pool.rate_type,
  benchmark_name: pool.benchmark_name || 'sofr',
  spread_bps: pool.spread_bps == null ? '' : String(pool.spread_bps),
  fixed_rate: pool.fixed_rate ?? '',
  day_count_convention: pool.day_count_convention,
  is_active: pool.is_active,
});

const labelClass = 'block text-xs text-gray-500 mb-1';
const controlClass =
  'w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500';

export const CashPoolModal: React.FC<CashPoolModalProps> = ({
  isOpen,
  onClose,
  product,
  pools,
  poolsUnavailable = false,
  mode = 'create',
  pool,
}) => {
  const isEditMode = mode === 'edit';
  const createPool = useCreateCashPool();
  const updatePool = useUpdateCashPool();
  const activeMutation = isEditMode ? updatePool : createPool;

  // `isError` matters as much as `data`: a failed benchmark fetch collapses the
  // select to the single seeded option and a failed counterparty fetch empties
  // it - both read as "the desk has none of these" rather than "the registry
  // didn't answer". Same class the pool list already has a banner for.
  const { data: benchmarks, isError: benchmarksUnavailable } = useBenchmarks();

  const baseline = useMemo(
    () =>
      isEditMode && pool ? buildEditForm(pool) : buildInitialForm(product),
    [isEditMode, pool, product]
  );
  const [formData, setFormData] = useState(baseline);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  // Every active counterparty is listed; the pool goes under its agreement of this
  // product, and one without that agreement is refused under the counterparty select. A high
  // limit rather than the registry page's default 100, so nobody is silently
  // dropped past the first page.
  // Not fetched in edit mode: the counterparty is immutable there and the select
  // names the pool's own row, so the request would be pure waste on every pencil.
  const { data: eligibleCounterparties, isError: counterpartiesUnavailable } =
    useCounterparties(
      {
        limit: 1000,
        is_active: true,
      },
      { enabled: !isEditMode }
    );

  // Diff what would actually be SENT, not the raw form. The rate branch that
  // isn't selected is never submitted (`buildRatePayload` omits it), so a
  // Fixed -> type a rate -> back to Floating round trip leaves the payload
  // byte-identical while the raw state differs - a flat key-by-key compare
  // pops "Discard Changes?" over a form that changed nothing, and traders
  // trained on false prompts click through the real ones.
  const hasFormChanges = useMemo(() => {
    const projected = (data: CashPoolFormData) =>
      JSON.stringify({
        // Not sent, but a pick of it is a change: a counterparty without the agreement
        // leaves `agreement` null.
        counterparty: data.counterparty,
        agreement: data.agreement,
        name: data.name.trim(),
        day_count_convention: data.day_count_convention,
        is_active: data.is_active,
        ...buildRatePayload(data),
      });
    return projected(formData) !== projected(baseline);
  }, [formData, baseline]);

  const handleCloseWithoutConfirmation = useCallback(() => {
    onClose();
    setShowCloseConfirmation(false);
    setAttemptedSubmit(false);
  }, [onClose]);

  const handleClose = useCallback(() => {
    if (hasFormChanges) {
      setShowCloseConfirmation(true);
    } else {
      handleCloseWithoutConfirmation();
    }
  }, [hasFormChanges, handleCloseWithoutConfirmation]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !showCloseConfirmation) {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey, true);
    }
    return () => document.removeEventListener('keydown', handleEscapeKey, true);
  }, [isOpen, handleClose, showCloseConfirmation]);

  const update = (changes: Partial<CashPoolFormData>) =>
    setFormData((prev) => ({ ...prev, ...changes }));

  /** The agreement's currency drives the accrual-days default until the trader
   *  overrides it. Once they have, picking another counterparty must not take it back. */
  const dayCountTouched = useRef(false);
  const handleCounterpartyChange = (value: string) => {
    const counterpartyId = value ? Number(value) : null;
    const agreement = (eligibleCounterparties ?? [])
      .find((cp) => cp.id === counterpartyId)
      ?.agreements.find((a) => a.type === formData.product);
    const currency = agreement?.currency ?? '';
    update({
      counterparty: counterpartyId,
      agreement: agreement?.apiId ?? null,
      currency,
      ...(dayCountTouched.current || !currency
        ? {}
        : {
            day_count_convention: String(
              dayCountForCurrency(currency)
            ) as CashPoolDayCount,
          }),
    });
  };

  const duplicate = useMemo(
    () => findDuplicatePool(pools, formData, pool?.id),
    [pools, formData, pool?.id]
  );

  /**
   * The endpoint serves only ACTIVE benchmarks, and the backend validates
   * against the same filter - so a pool still carrying a retired one (TONAR /
   * HONIA, dropped by references/0007) would render a blank select while the
   * form silently held the old value, and an unrelated edit would 400 naming a
   * field the trader never touched. Keep the stale value visible and flagged.
   * Ordered by the canonical list so the dropdown matches Trade Entry's.
   */
  const benchmarkRetired =
    formData.rate_type === 'floating' &&
    benchmarks !== undefined &&
    !benchmarks.some((b) => b.name === formData.benchmark_name);

  const benchmarkOptions = useMemo<string[]>(() => {
    const offered: string[] = (benchmarks ?? []).map((b) => b.name);
    const canonical: string[] = [...BENCHMARK_KEYS];
    const ordered = [
      ...canonical.filter((key) => offered.includes(key)),
      ...offered.filter((name) => !canonical.includes(name)),
    ];
    return ordered.includes(formData.benchmark_name)
      ? ordered
      : [formData.benchmark_name, ...ordered];
  }, [benchmarks, formData.benchmark_name]);

  const counterpartyInvalid = formData.counterparty == null;
  const agreementMissing = !counterpartyInvalid && formData.agreement == null;
  // Whole basis points: `step="1"` only constrains the spinner, so a typed or
  // pasted 25.5 would otherwise round-trip for a DRF "valid integer" 400.
  const spreadInvalid =
    formData.rate_type === 'floating' &&
    (formData.spread_bps.trim() === '' ||
      !Number.isInteger(Number(formData.spread_bps)));
  const fixedRateInvalid =
    formData.rate_type === 'fixed' &&
    (formData.fixed_rate.trim() === '' || Number(formData.fixed_rate) < 0);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setAttemptedSubmit(true);
    if (
      (!isEditMode && (counterpartyInvalid || agreementMissing)) ||
      spreadInvalid ||
      fixedRateInvalid
    ) {
      return;
    }
    const onSuccess = () => handleCloseWithoutConfirmation();
    if (isEditMode && pool) {
      updatePool.mutate({ id: pool.id, data: formData }, { onSuccess });
    } else {
      createPool.mutate(formData, { onSuccess });
    }
  };

  return (
    <>
      <Transition show={isOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={handleClose}>
          <TransitionChild
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" />
          </TransitionChild>

          <div className="fixed inset-0 z-[101] overflow-y-auto">
            <div className="flex min-h-full items-start justify-center p-4 py-10 sm:p-0 sm:py-10">
              <TransitionChild
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="relative flex w-full max-w-lg transform flex-col overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all">
                  <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <DialogTitle
                      as="h3"
                      className="text-lg font-semibold text-gray-900"
                    >
                      {isEditMode ? 'Edit Cash Pool' : 'New Cash Pool'}
                    </DialogTitle>
                    <button
                      type="button"
                      onClick={handleClose}
                      aria-label="Close"
                      className="rounded text-gray-400 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <form
                    onSubmit={handleSubmit}
                    className="flex-1 overflow-y-auto"
                  >
                    <div className="grid grid-cols-2 gap-4 p-6">
                      <div className="col-span-2">
                        <label
                          htmlFor="cash-pool-counterparty"
                          className={labelClass}
                        >
                          Counterparty <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="cash-pool-counterparty"
                          value={formData.counterparty ?? ''}
                          disabled={isEditMode}
                          onChange={(e) => handleCounterpartyChange(e.target.value)}
                          className={clsx(
                            controlClass,
                            isEditMode && 'bg-gray-50 text-gray-500'
                          )}
                        >
                          {/* Edit mode names the pool's own counterparty rather
                              than reading the registry: the dropdown lists only
                              ACTIVE counterparties, so a pool whose counterparty
                              was since soft-deleted would fall back to "Select
                              counterparty" over a row that names it - and the
                              same blank flashes while the query is in flight. */}
                          {isEditMode ? (
                            <option value={pool?.counterparty}>
                              {pool?.counterparty_name}
                            </option>
                          ) : (
                            <>
                              <option value="">Select counterparty</option>
                              {(eligibleCounterparties ?? []).map((cp) => (
                                <option key={cp.id} value={cp.id}>
                                  {cp.name}
                                </option>
                              ))}
                            </>
                          )}
                        </select>
                        {!isEditMode && counterpartiesUnavailable && (
                          <p className="mt-1 text-xs text-amber-700">
                            Couldn&apos;t load counterparties - the list is empty
                            because the registry failed, not because none match.
                          </p>
                        )}
                        {attemptedSubmit &&
                          !isEditMode &&
                          counterpartyInvalid && (
                            <p className="mt-1 text-xs text-red-600">
                              Counterparty is required
                            </p>
                          )}
                        {isEditMode && (
                          <p className="mt-1 text-xs text-gray-400">
                            Counterparty, currency and product are the pool
                            identity and cannot be changed.
                          </p>
                        )}
                        {!isEditMode && agreementMissing && (
                          <p role="alert" className="mt-1 text-xs text-red-600">
                            This counterparty has no {formData.product.toUpperCase()} agreement.
                            Add it in the counterparty&apos;s edit form, then create the pool.
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="cash-pool-currency"
                          className={labelClass}
                        >
                          Currency <span className="text-red-500">*</span>
                        </label>
                        {/* Not chosen: a pool is in its agreement's currency. */}
                        <input
                          id="cash-pool-currency"
                          value={formData.currency || '—'}
                          readOnly
                          disabled
                          className={clsx(controlClass, 'bg-gray-50 text-gray-500')}
                        />
                        {!isEditMode && (
                          <p className="mt-1 text-xs text-gray-400">
                            The {formData.product.toUpperCase()} agreement&apos;s currency.
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="cash-pool-day-count"
                          className={labelClass}
                        >
                          Accrual Days
                        </label>
                        <select
                          id="cash-pool-day-count"
                          value={formData.day_count_convention}
                          onChange={(e) => {
                            dayCountTouched.current = true;
                            update({
                              day_count_convention: e.target
                                .value as CashPoolDayCount,
                            });
                          }}
                          className={controlClass}
                        >
                          <option value="360">ACT/360</option>
                          <option value="365">ACT/365</option>
                        </select>
                      </div>

                      <div className="col-span-2">
                        <label htmlFor="cash-pool-name" className={labelClass}>
                          Pool Name
                        </label>
                        <input
                          id="cash-pool-name"
                          value={formData.name}
                          maxLength={255}
                          placeholder="Leave blank for CP · PRODUCT · CCY · rate definition"
                          onChange={(e) => update({ name: e.target.value })}
                          className={controlClass}
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          Shown in the Trade Entry dropdown.
                        </p>
                      </div>

                      {/* headlessui carries the full ARIA radio contract -
                          roving tabIndex plus arrow keys that move AND select.
                          Hand-rolled role="radio" buttons announce that contract
                          without honouring it: a screen-reader user hears
                          "1 of 2" and Arrow Right does nothing. */}
                      <RadioGroup
                        value={formData.rate_type}
                        onChange={(rate_type: CashPoolRateType) =>
                          update({ rate_type })
                        }
                        className="col-span-2"
                      >
                        <RadioLabel className={labelClass}>Rate Type</RadioLabel>
                        <div className="flex gap-1">
                          {RATE_TYPE_SEGMENTS.map((segment) => (
                            <RadioOption
                              key={segment.key}
                              value={segment.key}
                              className={({
                                checked,
                                active,
                              }: {
                                checked: boolean;
                                active: boolean;
                              }) => clsx(SEGMENT_CLASSES, segmentTone(checked, active))}
                            >
                              {segment.label}
                            </RadioOption>
                          ))}
                        </div>
                      </RadioGroup>

                      {formData.rate_type === 'floating' ? (
                        <>
                          <div>
                            <label
                              htmlFor="cash-pool-benchmark"
                              className={labelClass}
                            >
                              Benchmark <span className="text-red-500">*</span>
                            </label>
                            <select
                              id="cash-pool-benchmark"
                              value={formData.benchmark_name}
                              onChange={(e) =>
                                update({ benchmark_name: e.target.value })
                              }
                              className={controlClass}
                            >
                              {benchmarkOptions.map((name) => (
                                <option key={name} value={name}>
                                  {name.toUpperCase()}
                                </option>
                              ))}
                            </select>
                            {benchmarksUnavailable && (
                              <p className="mt-1 text-xs text-amber-700">
                                Couldn&apos;t load benchmarks - only the current
                                one is listed.
                              </p>
                            )}
                            {benchmarkRetired && (
                              <p className="mt-1 text-xs text-amber-700">
                                No longer offered - pick another before saving.
                              </p>
                            )}
                          </div>
                          <div>
                            <label
                              htmlFor="cash-pool-spread"
                              className={labelClass}
                            >
                              Spread (bps){' '}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              id="cash-pool-spread"
                              type="number"
                              step="1"
                              value={formData.spread_bps}
                              placeholder="25"
                              onChange={(e) =>
                                update({ spread_bps: e.target.value })
                              }
                              className={controlClass}
                            />
                            {attemptedSubmit && spreadInvalid && (
                              <p className="mt-1 text-xs text-red-600">
                                Whole basis points required
                              </p>
                            )}
                          </div>
                        </>
                      ) : (
                        <div>
                          <label
                            htmlFor="cash-pool-fixed-rate"
                            className={labelClass}
                          >
                            Fixed Rate (%){' '}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="cash-pool-fixed-rate"
                            type="number"
                            step="0.0001"
                            min="0"
                            value={formData.fixed_rate}
                            placeholder="4.5000"
                            onChange={(e) =>
                              update({ fixed_rate: e.target.value })
                            }
                            className={controlClass}
                          />
                          {attemptedSubmit && fixedRateInvalid && (
                            <p className="mt-1 text-xs text-red-600">
                              A non-negative rate is required
                            </p>
                          )}
                        </div>
                      )}

                      {isEditMode && (
                        <label className="col-span-2 flex cursor-pointer items-center gap-2 pt-1">
                          <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) =>
                              update({ is_active: e.target.checked })
                            }
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Active
                          </span>
                        </label>
                      )}
                    </div>

                    {/* "Couldn't look" must not render like "nothing matches":
                        the warning is computed from the page's pool list, so a
                        failed fetch silently disarms the one guard against a
                        duplicate the backend will happily accept. */}
                    {poolsUnavailable && (
                      <div
                        role="status"
                        className="mx-6 mb-4 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2"
                      >
                        <ExclamationTriangleIcon
                          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                          aria-hidden
                        />
                        <p className="text-xs text-amber-900">
                          Couldn&apos;t check for an existing pool with these
                          conditions - the pool list failed to load.
                        </p>
                      </div>
                    )}

                    {/* Product answer #13: an identical pool is allowed - warn, don't block. */}
                    {!poolsUnavailable && duplicate && (
                      <div
                        role="status"
                        className="mx-6 mb-4 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2"
                      >
                        <InformationCircleIcon
                          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                          aria-hidden
                        />
                        <p className="text-xs text-amber-900">
                          Cash pool <strong>{duplicate.name}</strong> already
                          exists with these conditions (
                          {describePoolTerms(duplicate)}). Save to create
                          another, or cancel and use the existing one.
                        </p>
                      </div>
                    )}

                    {activeMutation.isError && (
                      <div
                        role="alert"
                        className="mx-6 mb-4 flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2"
                      >
                        <ExclamationTriangleIcon
                          className="mt-0.5 h-4 w-4 shrink-0 text-red-600"
                          aria-hidden
                        />
                        <p className="text-xs text-red-800">
                          {getErrorMessage(
                            activeMutation.error,
                            'Failed to save. Please try again.'
                          )}
                        </p>
                      </div>
                    )}

                    <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-4">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        isLoading={activeMutation.isPending}
                      >
                        {isEditMode ? 'Save Changes' : 'Create Cash Pool'}
                      </Button>
                    </div>
                  </form>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition show={showCloseConfirmation} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-[110]"
          onClose={() => setShowCloseConfirmation(false)}
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl">
                <div className="p-6">
                  <DialogTitle className="mb-2 text-lg font-semibold text-gray-900">
                    Discard Changes?
                  </DialogTitle>
                  <p className="mb-6 text-sm text-gray-600">
                    You have unsaved changes. Are you sure you want to close
                    without saving?
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowCloseConfirmation(false)}
                      className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Continue Editing
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCloseConfirmation(false);
                        handleCloseWithoutConfirmation();
                      }}
                      className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      Discard Changes
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
